import _ from 'lodash';
import { useMemo } from 'react';
import { KEY_RECOIL, TEMPLATE_SOURCE_OPTIONS } from '@/shared/config';
import { useMergeState } from '@/shared/lib/customHooks';
import { handleMarkReviewByTemplateOnly } from '@/widgets/beats/ui/reviewBeatsHelper';
import {
  selectActiveTab,
  selectSelectedTemplate,
  selectTemplateChanges,
  useReportStore,
} from '@/entities/report';
import { formatMutationTemplate } from './helper';

/**
 * Backs the template-overview action bar: source filtering, review marking and include-in-report toggling for the selected morphology template. Reads the tab-1 keyed template state, swaps the view source from the filter menu key, rewrites cloned bulk reviewed, reset and per-beat review lists through handleMarkReviewByTemplateOnly, and opens a confirmation modal rather than committing once more than five templates would end up included.
 *
 * @returns An object carrying the view source, selected template, active tab, modal state and its setter, the filter, review and include handlers, and the derived isReviewed and isIncludeReport flags.
 */
const useTemplateAction = () => {
  const keyRecoil = KEY_RECOIL.BEAT_HR;
  const viewBeatsSource = useReportStore((s) => s.viewBeatsSource);
  const setViewBeatsSource = useReportStore((s) => s.actions.setViewBeatsSource);
  const templateChanges = useReportStore(selectTemplateChanges(keyRecoil));
  const setTemplateChangesAction = useReportStore((s) => s.actions.setTemplateChanges);
  /**
   * Writes the pending include-in-report edits on morphology templates. Forwards the value or updater to the store action with the tab-1 key already bound.
   *
   * @param v - The next value, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setTemplateChanges = (v: Parameters<typeof setTemplateChangesAction>[1]) =>
    setTemplateChangesAction(keyRecoil, v);

  const selectedTemplate = useReportStore(selectSelectedTemplate(keyRecoil));
  const setSelectedTemplateAction = useReportStore((s) => s.actions.setSelectedTemplate);
  /**
   * Replaces the morphology template selected in the overview. Forwards the value or updater to the store action with the tab-1 key already bound.
   *
   * @param v - The next value, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setSelectedTemplate = (v: Parameters<typeof setSelectedTemplateAction>[1]) =>
    setSelectedTemplateAction(keyRecoil, v);
  const bulkReviewedBeats = useReportStore((s) => s.bulkReviewedBeats);
  const setBulkReviewedBeats = useReportStore((s) => s.actions.setBulkReviewedBeats);
  const resetBulkReviewedBeats = useReportStore((s) => s.resetBulkReviewedBeats);
  const setResetBulkReviewedBeats = useReportStore((s) => s.actions.setResetBulkReviewedBeats);
  const reviewedBeatsChanges = useReportStore((s) => s.reviewedBeatsChanges);
  const setReviewedBeatsChanges = useReportStore((s) => s.actions.setReviewedBeatsChanges);
  const activeButton = useReportStore(selectActiveTab(keyRecoil));
  const setBeatOptionsAction = useReportStore((s) => s.actions.setBeatOptions);
  /**
   * Writes the beat option bundle mirrored into the beats tab state. Forwards the value or updater to the store action with the tab-1 key already bound.
   *
   * @param v - The next value, or an updater receiving the previous one.
   * @returns Nothing.
   */
  const setBeatOptions = (v: Parameters<typeof setBeatOptionsAction>[1]) =>
    setBeatOptionsAction(keyRecoil, v);
  /**
   * Reads the whole-study morphology template overview cached per beat type. Pulls originalTemplateOverview off a report-store snapshot rather than subscribing to it.
   *
   * @returns The template overview keyed by beat type.
   */
  const getOriginalTemplateOverview = () => useReportStore.getState().originalTemplateOverview;
  const originalTemplateOverview = useReportStore((s) => s.originalTemplateOverview);

  const [state, setState] = useMergeState({
    isOpenIncludeModal: false,
    isOpenExcludeModal: false,
  });

  /**
   * Switches the template overview between showing every template and only those behind the selected cells. Strips the index out of the menu key, applies the matching entry of TEMPLATE_SOURCE_OPTIONS as the view source, and clears the current template selection.
   *
   * @param info - Menu selection carrying the template-filter key.
   * @returns Nothing.
   */
  const onChangeFilter = (info: { key: string }) => {
    const { key } = info || {};
    const filter = key.replace('template-filter-', '');
    const newSource = TEMPLATE_SOURCE_OPTIONS[parseInt(filter, 10)];
    setViewBeatsSource(newSource);
    setSelectedTemplate({});
  };

  /**
   * Marks every beat of the selected morphology template reviewed or unreviewed. Deep-clones the bulk reviewed, reset and per-beat review lists, lets handleMarkReviewByTemplateOnly rewrite them for the active beat type, then stores all three back and mirrors them into the tab's beat options; does nothing while no template is selected.
   *
   * @param isReviewed - True to mark the template's beats reviewed, false to clear the marking.
   * @returns Nothing.
   */
  const onClickMarkReview = (isReviewed: boolean) => {
    if (_.isEmpty(selectedTemplate)) return;
    const cloneBulkReview = _.cloneDeep(bulkReviewedBeats);
    const cloneResetList = _.cloneDeep(resetBulkReviewedBeats);
    const cloneReviewBeatChanges = _.cloneDeep(reviewedBeatsChanges);

    handleMarkReviewByTemplateOnly({
      selectedTemplate,
      cloneBulkReview,
      cloneResetList,
      cloneReviewBeatChanges,
      isReviewed,
      activeButton,
    });

    setBulkReviewedBeats(cloneBulkReview);
    setResetBulkReviewedBeats(cloneResetList);
    setReviewedBeatsChanges(cloneReviewBeatChanges);
    setBeatOptions((prev: any) => ({
      ...prev,
      bulkReviewedBeatsState: cloneBulkReview,
      resetBulkReviewedBeatsState: cloneResetList,
      reviewedBeatsChangesState: cloneReviewBeatChanges,
    }));
  };
  /**
   * Toggles whether the selected morphology template is included in the printed report. Rebuilds the pending template changes by adding an entry, or by removing it and re-adding it only when the flag differs from the original data, then counts the resulting inclusions and opens the confirmation modal instead of committing once more than five templates would be included.
   *
   * @param isInclude - True to include the template in the report, false to exclude it.
   * @returns Nothing.
   */
  const onClickMarkInclude = (isInclude: boolean) => {
    const cloneTemplateChanges = _.cloneDeep(templateChanges);
    const findChange = _.cloneDeep(_.find(templateChanges, (x) => x.id === selectedTemplate.id));
    if (!findChange) {
      cloneTemplateChanges.push({
        ...selectedTemplate,
        ...selectedTemplate?.originalData,
        isIncludedToReport: isInclude,
      });
    } else {
      _.remove(cloneTemplateChanges, (x) => x.id === selectedTemplate.id);
      if (selectedTemplate.originalData.isIncludedToReport !== isInclude) {
        cloneTemplateChanges.push({
          ...selectedTemplate,
          ...selectedTemplate?.originalData,
          isIncludedToReport: isInclude,
        });
      }
    }
    const originalTemplate = getOriginalTemplateOverview()[activeButton] || [];
    const { selectedTemplates } = formatMutationTemplate({
      updateIncludeReport: cloneTemplateChanges,
      morphologyTemplate: originalTemplate,
    });
    if (selectedTemplates.length > 5) {
      setState({ isOpenIncludeModal: true });
      return;
    }
    setTemplateChanges(cloneTemplateChanges);
  };

  const isReviewed = useMemo(() => {
    if (_.isEmpty(selectedTemplate)) return false;
    const totalBeats = selectedTemplate.total || 0;
    let reviewedCount = selectedTemplate.reviewed || 0;
    _.forEach(bulkReviewedBeats, (item) => {
      if (item.originalTemplateId && item.originalTemplateId === selectedTemplate.id) {
        const originalReviewed = item.originalReviewedCount || 0;
        const totalCount = item.totalCount || 0;
        if (item.isReviewed) {
          reviewedCount += totalCount - originalReviewed;
        } else {
          reviewedCount -= originalReviewed;
        }
        const filterReset = _.filter(
          resetBulkReviewedBeats,
          (x) => x.originalTemplateId === selectedTemplate.id && x.cellId === item.cellId,
        );
        if (filterReset.length > 0) {
          if (item.isReviewed) {
            reviewedCount -= filterReset.length;
          } else {
            reviewedCount += filterReset.length;
          }
        }
      }
    });
    _.forEach(reviewedBeatsChanges, (item) => {
      if (item.originalTemplateId === selectedTemplate.id) {
        if (item.isReviewed) {
          reviewedCount += 1;
        } else {
          reviewedCount -= 1;
        }
      }
    });
    return reviewedCount === totalBeats;
  }, [
    selectedTemplate,
    reviewedBeatsChanges,
    bulkReviewedBeats,
    resetBulkReviewedBeats,
    originalTemplateOverview,
  ]);

  const isIncludeReport = useMemo(() => {
    if (_.isEmpty(selectedTemplate)) return false;
    const findChange = _.find(templateChanges, (x) => x.id === selectedTemplate.id);
    if (findChange) {
      return findChange.isIncludedToReport;
    }
    return selectedTemplate.originalData.isIncludedToReport;
  }, [selectedTemplate, templateChanges]);

  return {
    viewBeatsSource,
    selectedTemplate,
    activeButton,
    state,
    setState,
    onChangeFilter,
    onClickMarkReview,
    onClickMarkInclude,
    isReviewed,
    isIncludeReport,
  };
};

export default useTemplateAction;
